import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * MetaTags component that fetches and applies SEO settings for a page
 * @param {string} pageId - The page identifier (e.g., "home", "schedule")
 * @param {string} defaultTitle - Fallback title if no SEO settings exist
 * @param {string} defaultDescription - Fallback description
 * @param {string} defaultImage - Fallback OG image
 */
export const MetaTags = ({ 
  pageId, 
  defaultTitle = "Antler Sports Network", 
  defaultDescription = "Your home for high school and collegiate sports coverage",
  defaultImage = ""
}) => {
  const [seo, setSeo] = useState(null);
  const [siteSettings, setSiteSettings] = useState(null);

  useEffect(() => {
    const fetchSEO = async () => {
      try {
        const [seoRes, siteRes] = await Promise.all([
          axios.get(`${API}/settings/pages/seo/${pageId}`),
          axios.get(`${API}/settings/site`)
        ]);
        setSeo(seoRes.data);
        setSiteSettings(siteRes.data);
      } catch (err) {
        // Use defaults if fetch fails
      }
    };
    fetchSEO();
  }, [pageId]);

  const siteName = "Antler Sports Network";
  // Title is managed by PageTitleManager in App.js
  const description = seo?.meta_description || defaultDescription;
  const ogTitle = seo?.og_title || siteName;
  const ogDescription = seo?.og_description || description;
  const ogImage = seo?.og_image || defaultImage || siteSettings?.logo_url;
  const twitterCard = seo?.twitter_card || "summary_large_image";
  const canonicalUrl = seo?.canonical_url || window.location.href;
  const noIndex = seo?.no_index || false;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <meta name="description" content={description} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:title" content={ogTitle} />
      <meta property="og:description" content={ogDescription} />
      {ogImage && <meta property="og:image" content={ogImage} />}
      <meta property="og:url" content={window.location.href} />

      {/* Twitter */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={ogTitle} />
      <meta name="twitter:description" content={ogDescription} />
      {ogImage && <meta name="twitter:image" content={ogImage} />}
    </Helmet>
  );
};

export default MetaTags;
